import { useEffect, useMemo, useState } from "react";
import { apiDiagnose, apiReportRtf, apiTeachers, type DiagnoseResponse, type TeacherItem } from "./lib/api";

type Step = "invite" | "input" | "result";
type ProgressPhase = "idle" | "reading" | "analyzing" | "writing";

const GENRE_OPTIONS = ["记叙文", "抒情文", "议论文", "说明文", "日记", "随笔", "仪式文"] as const;
type Genre = (typeof GENRE_OPTIONS)[number];

const GRADE_OPTIONS = [
  "",
  "三年级",
  "四年级",
  "五年级",
  "六年级",
  "七年级/初一",
  "八年级/初二",
  "九年级/初三",
] as const;
type Grade = (typeof GRADE_OPTIONS)[number];

const SAMPLE_GRADE: Grade = "四年级";
const SAMPLE_TITLE = "那一次，我学会了坚持";
const SAMPLE_ESSAY = `那天放学后，操场上吹着冷风。我和同学约好练跳绳，因为下周要比赛。
一开始我总是跳不到十下就绊住，绳子啪地打在腿上，火辣辣的。我有点想放弃，心里嘀咕：我是不是不适合跳绳？

同学说："慢一点，先把节奏找回来。"我就深吸一口气，数着"一、二、三……"，手腕轻轻一转，脚尖像小鸟一样点地。我还是会失败，但我不再急着生气，而是每次都想一想：是手太高了，还是脚抬得不够？

天快黑了，我终于连续跳到一百下。我累得满头汗，却特别开心。回家的路上，我觉得坚持就像把一粒小种子埋进土里，只要每天浇一点水，它就会慢慢长大。`;

const PROGRESS_PHASES: { phase: ProgressPhase; icon: string; text: string; subtext: string }[] = [
  { phase: "reading", icon: "📖", text: "老师正在阅读作文...", subtext: "认真读每一个字" },
  { phase: "analyzing", icon: "🔍", text: "老师正在分析问题...", subtext: "找出可以改进的地方" },
  { phase: "writing", icon: "✍️", text: "老师正在撰写建议...", subtext: "用心写下点评和建议" },
];

// 类型定义：诊断结果中的问题项
interface ScanResult {
  rule_id?: string;
  rule_name?: string;
  passed?: boolean;
  severity?: "high" | "medium" | "low";
  trigger_scope?: string;
  evidence_quote?: string;
  evidence_location?: string;
  short_reason?: string;
}

// 问题与建议
interface IssueAdvice {
  title?: string;
  severity?: string;
  problem?: string;
  evidence?: string;
  fixes?: string[];
  think_about?: string[];
}

interface RewriteCompare {
  original?: string;
  // v1 契约（后端返回）
  paragraph_index?: number;
  normal_rewrite?: string;
  master_rewrite?: string;
  why_better?: string;

  // 兼容旧字段（前端历史字段）
  paragraph?: string;
  normal_fix?: string;
  master_fix?: string;
  explanation?: string;
  // 兼容旧字段
  improved?: string;
}

interface Report {
  overall_summary?: string;
  // 后端契约：top_issues[*] = { rule_id, diagnosis, actions, ask_questions }
  top_issues?: ReportTopIssue[];
  bonus_tips?: string[];
  rewrite_compare?: RewriteCompare;
}

// 辅助函数：severity 映射
function getSeverityLabel(severity?: string): string {
  switch (severity) {
    case "high": return "重点问题";
    case "medium": return "一般问题";
    case "low": return "细节/润色";
    default: return "一般问题";
  }
}

type ReportTopIssue = {
  rule_id?: string;
  // 方案 A：后端返回规则中文名与严重度，前端不展示 rule_id
  rule_name?: string;
  severity?: "high" | "medium" | "low" | string;
  diagnosis?: string;
  actions?: string[];
  ask_questions?: string[];
};

// 辅助函数：trigger_scope 映射
function getScopeLabel(scope?: string): string {
  const s = String(scope || "").trim();
  switch (scope) {
    // 统一口径：全文 / 段落 / 句子（兼容后端/历史字段）
    case "document":
    case "essay":
    case "整篇作文":
    case "全文":
      return "全文";
    case "paragraph":
    case "某一段":
    case "段落":
      return "段落";
    case "sentence":
    case "一句话":
    case "句子":
      return "句子";
    default:
      return s;
  }
}

export function App() {
  const [step, setStep] = useState<Step>("invite");
  const [inviteCode, setInviteCode] = useState<string>(() => localStorage.getItem("m2s_invite_code_v1") || "");
  const [teachers, setTeachers] = useState<TeacherItem[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teacherNs, setTeacherNs] = useState<string>("");

  const [grade, setGrade] = useState<Grade>(() => {
    const v = String(localStorage.getItem("m2s_grade_v1") || "").trim();
    return (GRADE_OPTIONS as readonly string[]).includes(v) ? (v as Grade) : "";
  });
  const [title, setTitle] = useState("");
  const [genres, setGenres] = useState<Genre[]>([]);
  const [essayText, setEssayText] = useState("");

  const [busy, setBusy] = useState(false);
  const [progressPhase, setProgressPhase] = useState<ProgressPhase>("idle");
  const [err, setErr] = useState<string>("");
  const [diag, setDiag] = useState<DiagnoseResponse | null>(null);
  const [highlightQuote, setHighlightQuote] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackType, setFeedbackType] = useState("申请邀请码");

  const domain = "K12作文";
  const domainSlug = "k12_essay";

  const selectedTeacher = useMemo(() => teachers.find((t) => t.namespace === teacherNs) || null, [teachers, teacherNs]);

  const filteredTeachers = useMemo(() => {
    if (!genres.length) return teachers;
    return teachers.filter((t) => genres.every((g) => (t.genres || []).includes(g)));
  }, [teachers, genres]);

  async function loadTeachers(code: string) {
    setLoadingTeachers(true);
    setErr("");
    try {
      const resp = await apiTeachers(code);
      setTeachers(resp.teachers || []);
      const prefer = resp.teachers?.find((t) => t.display_name === "王鼎钧") || resp.teachers?.[0];
      setTeacherNs(prefer?.namespace || "");
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoadingTeachers(false);
    }
  }

  useEffect(() => {
    if (step === "input" && teachers.length === 0 && inviteCode.trim()) {
      void loadTeachers(inviteCode.trim());
    }
  }, [step, teachers.length, inviteCode]);

  useEffect(() => {
    if (step !== "input") return;
    if (loadingTeachers) return;
    if (!teachers.length) return;

    if (!filteredTeachers.length) {
      if (teacherNs) setTeacherNs("");
      return;
    }
    if (!teacherNs || !filteredTeachers.some((t) => t.namespace === teacherNs)) {
      const prefer = filteredTeachers.find((t) => t.display_name === "王鼎钧") || filteredTeachers[0];
      setTeacherNs(prefer?.namespace || "");
    }
  }, [step, loadingTeachers, teachers.length, filteredTeachers, teacherNs]);

  function enter() {
    const code = inviteCode.trim();
    if (!code) {
      setErr("请输入邀请码");
      return;
    }
    localStorage.setItem("m2s_invite_code_v1", code);
    setErr("");
    setStep("input");
  }

  function setGradeAndPersist(v: Grade) {
    setGrade(v);
    localStorage.setItem("m2s_grade_v1", v);
  }

  function toggleGenre(g: Genre) {
    setGenres((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  }

  function applySample() {
    setErr("");
    setEssayText(SAMPLE_ESSAY);
    setTitle(SAMPLE_TITLE);
    setGradeAndPersist(SAMPLE_GRADE);
    if (!genres.length) setGenres(["记叙文"]);
  }

  async function runDiagnose() {
    const code = inviteCode.trim();
    if (!code) {
      setErr("邀请码为空");
      setStep("invite");
      return;
    }
    if (!teacherNs) {
      setErr("请先选择老师");
      return;
    }
    if (!essayText.trim()) {
      setErr("请先粘贴作文");
      return;
    }

    setBusy(true);
    setErr("");
    
    // 模拟进度展示
    setProgressPhase("reading");
    const phaseTimer1 = setTimeout(() => setProgressPhase("analyzing"), 2000);
    const phaseTimer2 = setTimeout(() => setProgressPhase("writing"), 4500);

    try {
      const resp = await apiDiagnose(code, {
        domain,
        domain_slug: domainSlug,
        namespace: teacherNs,
        grade: grade || undefined,
        title: title.trim() || undefined,
        target_genre: genres.length ? (genres.length === 1 ? genres[0] : genres.join("、")) : undefined,
        essay_text: essayText,
      });
      setDiag(resp);
      setStep("result");
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      clearTimeout(phaseTimer1);
      clearTimeout(phaseTimer2);
      setProgressPhase("idle");
      setBusy(false);
    }
  }

  async function downloadRtf() {
    const code = inviteCode.trim();
    if (!code || !diag) return;
    setBusy(true);
    setErr("");
    try {
      // 传递完整的诊断结果
      const blob = await apiReportRtf(code, diag);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "老师作文诊断报告.rtf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  // 渲染带高亮的作文文本
  function renderEssayWithHighlight(text: string, quote: string | null) {
    if (!quote) return text;
    
    // 清理引号和省略号，提取关键片段
    const rawFragments = quote
      .replace(/^["'""''「」『』【】]/g, "")  // 移除首部各种引号
      .replace(/["'""''「」『』【】]$/g, "")  // 移除尾部各种引号
      .replace(/[……\.]{2,}/g, "|||SPLIT|||")  // 分割省略号
      .split("|||SPLIT|||")
      .map(s => s.trim())
      .filter(s => s.length > 2);
    
    if (!rawFragments.length) return text;
    
    // 收集可以在原文中匹配到的片段
    const matchableFragments: string[] = [];
    
    for (const fragment of rawFragments) {
      // 策略1：尝试精确匹配
      if (text.includes(fragment)) {
        matchableFragments.push(fragment);
        continue;
      }
      
      // 策略2：提取连续的中文字符片段（至少4个字），忽略标点差异
      const chineseChunks = fragment.match(/[\u4e00-\u9fa5]{4,}/g) || [];
      for (const chunk of chineseChunks) {
        if (text.includes(chunk) && !matchableFragments.includes(chunk)) {
          matchableFragments.push(chunk);
        }
      }
    }
    
    if (!matchableFragments.length) return text;
    
    // 按长度降序排列，优先匹配长片段
    matchableFragments.sort((a, b) => b.length - a.length);
    
    // 构建正则匹配所有片段
    const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = matchableFragments.map(escapeRegex).join('|');
    const regex = new RegExp(`(${pattern})`, 'g');
    
    const parts = text.split(regex);
    
    // 创建一个 Set 用于快速查找
    const fragmentSet = new Set(matchableFragments);
    
    let firstHighlight = true;
    return parts.map((part, i) => {
      // 检查 part 是否是可匹配片段之一
      const isHighlight = fragmentSet.has(part);
      if (isHighlight) {
        // 第一个高亮元素添加 id，用于滚动定位
        const isFirst = firstHighlight;
        firstHighlight = false;
        return <mark key={i} id={isFirst ? "highlight-anchor" : undefined} className="essay-highlight">{part}</mark>;
      }
      return part;
    });
  }

  // 点击证据高亮
  function handleQuoteClick(quote: string | undefined) {
    if (!quote) return;
    // 点击同一个则取消高亮
    setHighlightQuote(prev => prev === quote ? null : quote);
  }

  // 处理反馈表单提交
  async function handleFeedbackSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFeedbackSubmitting(true);
    const form = e.currentTarget;
    try {
      const resp = await fetch("https://formspree.io/f/xpqaovql", {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (resp.ok) {
        setFeedbackSuccess(true);
        setTimeout(() => {
          setFeedbackOpen(false);
          setFeedbackSuccess(false);
        }, 2000);
      } else {
        alert("提交失败，请稍后重试");
      }
    } catch {
      alert("网络错误，请稍后重试");
    } finally {
      setFeedbackSubmitting(false);
    }
  }

  // 高亮变化时自动滚动到高亮位置（只滚动 essay-panel 容器，不影响整体页面）
  useEffect(() => {
    if (!highlightQuote) return;
    // 稍微延迟，等待 DOM 更新
    const timer = setTimeout(() => {
      const anchor = document.getElementById("highlight-anchor");
      const container = document.getElementById("essay-panel-container");
      if (anchor && container) {
        // 计算高亮元素在容器内的相对位置，滚动容器而非整个页面
        const anchorRect = anchor.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const offsetInContainer = anchorRect.top - containerRect.top + container.scrollTop;
        const targetScroll = offsetInContainer - container.clientHeight / 2 + anchor.clientHeight / 2;
        container.scrollTo({ top: Math.max(0, targetScroll), behavior: "smooth" });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [highlightQuote]);

  // 渲染进度覆盖层
  function renderProgressOverlay() {
    if (progressPhase === "idle") return null;
    const phaseInfo = PROGRESS_PHASES.find((p) => p.phase === progressPhase);
    if (!phaseInfo) return null;

    return (
      <div className="progress-overlay">
        <div className="progress-card">
          <div className="progress-icon">{phaseInfo.icon}</div>
          <div className="progress-text">{phaseInfo.text}</div>
          <div className="progress-subtext">{phaseInfo.subtext}</div>
          <div className="progress-bar">
            <div className="progress-bar-fill" />
          </div>
        </div>
      </div>
    );
  }

  // 渲染诊断结果
  function renderResult() {
    if (!diag) return null;

    const report = (diag.report || {}) as Report & Record<string, unknown>;
    const scanResultsAll = (diag.scan_results || []) as ScanResult[];
    const meta = diag.meta as Record<string, unknown>;
    
    // 对齐 essay_app.py：隐藏"不适用"检查项，避免噪音与"通过/不适用"错觉
    const scanResultsFiltered = scanResultsAll.filter((r) => !String(r.short_reason || "").includes("不适用"));

    // 排序：未通过项按严重程度排前（high > medium > low），通过项排后
    const severityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const scanResults = [...scanResultsFiltered].sort((a, b) => {
      // 未通过排前，通过排后
      if (a.passed !== b.passed) return a.passed ? 1 : -1;
      // 都未通过时，按严重程度排序
      if (!a.passed && !b.passed) {
        const aOrder = severityOrder[a.severity || "medium"] ?? 1;
        const bOrder = severityOrder[b.severity || "medium"] ?? 1;
        return aOrder - bOrder;
      }
      return 0;
    });

    // 统计：所有检查项
    const totalChecks = scanResults.length;
    const passedChecks = scanResults.filter((r) => r.passed === true);
    const failedChecks = scanResults.filter((r) => r.passed === false);
    
    // 按严重程度统计失败项
    const highCount = failedChecks.filter((i) => i.severity === "high").length;
    const mediumCount = failedChecks.filter((i) => i.severity === "medium").length;
    const lowCount = failedChecks.filter((i) => i.severity === "low").length;

    return (
      <div className="result-container">
        {/* 老师总评 */}
        {report.overall_summary && (
          <div className="summary-card">
            <div className="summary-title">
              📝 {meta.teacher_name ? `${meta.teacher_name}老师的点评` : "老师点评"}
            </div>
            <div className="summary-content">{report.overall_summary}</div>
          </div>
        )}

        {/* 双栏布局：作文原文 | 检查列表 */}
        <div className="result-layout">
          {/* 左栏：作文原文 */}
          <div id="essay-panel-container" className="essay-panel" onClick={() => setHighlightQuote(null)}>
            <div className="essay-panel-title">
              作文原文
              {highlightQuote && <span className="essay-panel-hint">（点击空白处取消高亮）</span>}
            </div>
            <div className="essay-text">{renderEssayWithHighlight(essayText, highlightQuote)}</div>
          </div>

          {/* 右栏：检查项列表 */}
          <div className="issues-panel">
            {/* 紧凑统计 + 标题 */}
            <div className="issues-panel-header">
              <div className="issues-panel-title">老师本次重点检查</div>
              <div className="issues-panel-stats">
                <span className="stat-chip">共 {totalChecks} 项</span>
                <span className="stat-chip success">✓ {passedChecks.length}</span>
                <span className="stat-chip danger">! {failedChecks.length}</span>
              </div>
            </div>
            {scanResults.map((item, i) => (
              <div className={`check-item ${item.passed ? "" : `severity-${item.severity || "medium"}`}`} key={i}>
                <div className="check-item-header">
                  <div className={`check-item-status ${item.passed ? "passed" : "failed"} ${item.severity || ""}`}>
                    {item.passed ? "✓" : "!"}
                  </div>
                  <div className="check-item-title">
                    <div className="check-item-name">
                      {i + 1}. {item.rule_name || item.short_reason?.split("。")[0] || "检查项"}
                      {!item.passed && item.severity && (
                        <span className={`severity-tag ${item.severity}`}>{getSeverityLabel(item.severity)}</span>
                      )}
                    </div>
                    <div className="check-item-meta">
                      <span>{item.passed ? "做到了 🌟" : "需要改"}</span>
                      {item.trigger_scope && (
                        <>
                          <span>|</span>
                          <span>范围：{getScopeLabel(item.trigger_scope)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="check-item-body">
                  {item.evidence_location && (
                    <div className="check-item-location">📍 位置：{item.evidence_location}</div>
                  )}
                  {item.short_reason && (
                    <div className="check-item-reason">
                      {item.short_reason}
                    </div>
                  )}
                  {item.evidence_quote && (
                    <div
                      className={`check-item-quote clickable ${highlightQuote === item.evidence_quote ? "active" : ""}`}
                      onClick={() => handleQuoteClick(item.evidence_quote)}
                      title="点击在左侧原文中高亮"
                    >
                      🔍 证据："{item.evidence_quote}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 问题与建议 */}
        {(() => {
          // 后端契约：top_issues[*] = { rule_id, diagnosis, actions, ask_questions }
          const raw = ((report as any)?.top_issues || (report as any)?.topIssues || []) as unknown[];
          const topIssues: ReportTopIssue[] = Array.isArray(raw)
            ? (raw.filter((x) => x && typeof x === "object") as ReportTopIssue[])
            : [];

          // 用 scan_results 把 rule_id 补充成 severity / rule_name（若存在）
          const severityByRuleId = new Map<string, ScanResult>();
          for (const r of failedChecks) {
            if (r?.rule_id) severityByRuleId.set(r.rule_id, r);
          }

          const advices: IssueAdvice[] = topIssues
            .map((x) => {
              const diagnosis = String((x as any)?.diagnosis || "").trim();
              const actions = Array.isArray((x as any)?.actions) ? (x as any).actions.map(String).filter(Boolean) : [];
              const ask = Array.isArray((x as any)?.ask_questions)
                ? (x as any).ask_questions.map(String).filter(Boolean)
                : [];

              const ruleName = String((x as any)?.rule_name || "").trim();
              const ruleId = String((x as any)?.rule_id || "").trim();
              const extra = ruleId ? severityByRuleId.get(ruleId) : undefined;

              // 注意：按要求 UI 不展示 rule_id；没有 rule_name 时用通用标题兜底
              const title = (ruleName || extra?.rule_name || "改进建议").trim();
              const sev = ((x as any)?.severity || extra?.severity) as any;

              // 如果没有任何可展示内容，就跳过
              if (!diagnosis && !actions.length && !ask.length) return null;

              return {
                title,
                severity: sev,
                problem: diagnosis,
                fixes: actions,
                think_about: ask,
              };
            })
            .filter(Boolean) as IssueAdvice[];

          if (!advices.length) return null;

          return (
            <>
              <div className="section-divider">问题与建议</div>
              {advices.map((advice, i) => {
                const hasTitle = advice.title && String(advice.title).trim();
                const hasProblem = advice.problem && String(advice.problem).trim();
                const hasFixes = advice.fixes && advice.fixes.length > 0;
                const hasThink = advice.think_about && advice.think_about.length > 0;

                return (
                  <div className="advice-section" key={i}>
                    <div className="advice-header">
                      <span className={`advice-badge ${advice.severity || "medium"}`}>
                        {getSeverityLabel(advice.severity)}
                      </span>
                      <span className="advice-title">{hasTitle || "改进建议"}</span>
                    </div>

                    {hasProblem && (
                      <div className="advice-problem">
                        <div className="advice-problem-label">问题在哪：</div>
                        <div className="advice-problem-text">{advice.problem}</div>
                      </div>
                    )}

                    {hasFixes && (
                      <div className="advice-fix">
                        <div className="advice-fix-label">怎么改：</div>
                        <ul className="advice-fix-list">
                          {advice.fixes!.map((fix, j) => (
                            <li key={j}>{fix}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {hasThink && (
                      <div className="advice-think">
                        <div className="advice-think-label">想一想：</div>
                        <ul className="advice-think-list">
                          {advice.think_about!.map((q, j) => (
                            <li key={j}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          );
        })()}

        {/* 加分建议 */}
        {(report["bonus_tips"] as string[] | undefined) && (report["bonus_tips"] as string[]).length > 0 && (
          <>
            <div className="section-divider">加分建议（可选）</div>
            <div className="tips-section">
              <div className="tips-title">💡 老师的建议</div>
              <ul className="tips-list">
                {(report["bonus_tips"] as string[]).map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* 看看老师怎么改 */}
        {(() => {
          const rc = (report["rewrite_compare"] as any) || undefined;
          if (!rc || typeof rc !== "object") return null;

          const original = String(rc.original || "").trim();
          const normal = String(rc.normal_rewrite || rc.normal_fix || "").trim();
          const master = String(rc.master_rewrite || rc.master_fix || rc.improved || "").trim();
          const why = String(rc.why_better || rc.explanation || "").trim();
          const idx = Number.isFinite(Number(rc.paragraph_index)) ? Number(rc.paragraph_index) : undefined;

          if (!original && !normal && !master) return null;

          return (
          <>
            <div className="section-divider">看看老师怎么改</div>
            <div className="rewrite-section-full">
              <div className="rewrite-section-title">✨ 老师的改写建议</div>
              {idx !== undefined && idx >= 0 && (
                <div className="rewrite-section-subtitle">修改的段落：第 {idx + 1} 段</div>
              )}
              
              <div className="rewrite-grid">
                {original && (
                  <div className="rewrite-item original">
                    <div className="rewrite-item-label">📄 你写的原文</div>
                    {original}
                  </div>
                )}
                
                {normal && (
                  <div className="rewrite-item normal">
                    <div className="rewrite-item-label">✏️ 普通修改</div>
                    {normal}
                  </div>
                )}
                
                {master && (
                  <div className="rewrite-item master">
                    <div className="rewrite-item-label">🌟 名师修改</div>
                    {master}
                  </div>
                )}
              </div>
              
              {why && (
                <div className="rewrite-explanation">
                  <strong>💡 名师修改更好在哪：</strong>{why}
                </div>
              )}
            </div>
          </>
          );
        })()}
      </div>
    );
  }

  return (
    <div className="container">
      {renderProgressOverlay()}

      <div className="header">
        <div>
          <div className="title">名师点拨作文（K12）</div>
          <div className="hint">粘贴作文 → 选择老师 → 获取点评和建议</div>
        </div>
        <div className="badge">内测</div>
      </div>

      {err ? <div className="card error">{err}</div> : null}

      {step === "invite" ? (
        <div className="card">
          <div className="row">
            <div className="field">
              <label>邀请码</label>
              <input
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="可点击下方申请邀请码"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && enter()}
              />
            </div>
          </div>
          <div className="btnRow">
            <button className="primary" onClick={enter}>
              开始使用
            </button>
          </div>
          <div className="hint" style={{ marginTop: 12 }}>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setFeedbackType("申请邀请码"); setFeedbackOpen(true); }}
              style={{ color: "#6b7280", textDecoration: "underline", cursor: "pointer" }}
            >
              申请邀请码 / 建议或反馈
            </a>
          </div>
        </div>
      ) : null}

      {step === "input" ? (
        <div className="card">
          {/* 1. 作文正文 - 最重要，放在最前面 */}
          <div className="row">
            <div className="field" style={{ flexBasis: "100%" }}>
              <div className="field-header">
                <label>作文正文</label>
                <span className="field-actions">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={applySample}
                    className="btn-small"
                  >
                    看看示例
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => { setEssayText(""); setTitle(""); }}
                    className="btn-small"
                  >
                    清空
                  </button>
                </span>
              </div>
              <textarea
                value={essayText}
                onChange={(e) => setEssayText(e.target.value)}
                placeholder="把作文粘贴到这里，老师会认真阅读每一个字..."
                disabled={busy}
              />
            </div>
          </div>

          {/* 2. 题目和年级 */}
          <div className="row">
            <div className="field">
              <label>作文题目（可不填）</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="输入作文的题目"
                disabled={busy}
              />
            </div>
            <div className="field">
              <label>年级（可不填）</label>
              <select value={grade} onChange={(e) => setGradeAndPersist(e.target.value as Grade)} disabled={busy}>
                {GRADE_OPTIONS.map((x) => (
                  <option key={x || "__empty__"} value={x}>
                    {x || "（不填）"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 3. 目标文体 - 改为 checkbox 标签组 */}
          <div className="row">
            <div className="field" style={{ flexBasis: "100%" }}>
              <label>目标文体（可多选）</label>
              <div className="genre-tags">
                {GENRE_OPTIONS.map((g) => (
                  <label
                    key={g}
                    className={`genre-tag ${genres.includes(g) ? "selected" : ""}`}
                    onClick={() => !busy && toggleGenre(g)}
                  >
                    <input
                      type="checkbox"
                      checked={genres.includes(g)}
                      onChange={() => toggleGenre(g)}
                      disabled={busy}
                    />
                    <span>{g}</span>
                  </label>
                ))}
              </div>
              <div className="hint">
                不选 = 老师会根据内容判断文体
                {genres.length ? `；已选：${genres.join("、")}` : ""}
              </div>
            </div>
          </div>

          {/* 4. 选择老师 */}
          <div className="row">
            <div className="field">
              <label>选择老师</label>
              <select value={teacherNs} onChange={(e) => setTeacherNs(e.target.value)} disabled={loadingTeachers || busy}>
                {filteredTeachers.map((t) => (
                  <option key={t.namespace} value={t.namespace}>
                    {t.display_name}
                  </option>
                ))}
              </select>
              <div className="hint">
                {loadingTeachers
                  ? "正在加载老师列表..."
                  : selectedTeacher?.genres?.length
                  ? `${selectedTeacher.display_name}老师擅长：${selectedTeacher.genres.join("、")}`
                  : filteredTeachers.length
                  ? ""
                  : "当前文体组合暂无老师支持，请调整文体选择"}
              </div>
            </div>
          </div>

          <div className="btnRow">
            <button className="primary" disabled={busy || !teacherNs} onClick={runDiagnose}>
              让老师帮我看看
            </button>
          </div>
        </div>
      ) : null}

      {step === "result" && diag ? (
        <div className="card">
          {renderResult()}

          <div className="btnRow">
            <button className="primary" disabled={busy} onClick={downloadRtf}>
              {busy ? "生成中..." : "下载完整报告（RTF）"}
            </button>
            <button disabled={busy} onClick={() => setStep("input")}>
              返回修改作文
            </button>
          </div>
        </div>
      ) : null}

      {/* 反馈弹窗 */}
      {feedbackOpen && (
        <div className="modal-overlay" onClick={() => setFeedbackOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">申请邀请码 / 建议或反馈</span>
              <button className="modal-close" onClick={() => setFeedbackOpen(false)}>×</button>
            </div>
            {feedbackSuccess ? (
              <div className="modal-success">
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <div>提交成功，感谢您的反馈！</div>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit}>
                <div className="modal-field">
                  <label>类型</label>
                  <select name="type" required value={feedbackType} onChange={(e) => setFeedbackType(e.target.value)}>
                    <option value="申请邀请码">申请邀请码</option>
                    <option value="功能建议">功能建议</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div className="modal-field">
                  <label>内容{feedbackType !== "申请邀请码" ? "（可不填）" : ""}</label>
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="请描述您的需求或反馈..."
                  />
                </div>
                <div className="modal-field">
                  <label>邮箱 / 手机号{feedbackType === "申请邀请码" ? "" : "（可不填）"}</label>
                  <input
                    type="text"
                    name="contact"
                    required={feedbackType === "申请邀请码"}
                    placeholder="方便我们回复您"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setFeedbackOpen(false)}>取消</button>
                  <button type="submit" className="primary" disabled={feedbackSubmitting}>
                    {feedbackSubmitting ? "发送中..." : "发送"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
